import { useEffect, useState, useMemo } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import {
  Card,
  Tabs,
  Statistic,
  Row,
  Col,
  Grid,
  Space,
  Avatar,
  Spin,
  theme,
  Alert,
  Typography,
  Table,
  Button,
  Select,
  Tag,
  Modal,
  Descriptions,
  Empty,
  Popconfirm,
  List,
  Divider,
  Popover,
  Tooltip
} from "antd"
import {
  SafetyOutlined,
  BarChartOutlined,
  BankOutlined,
  TeamOutlined,
  UserOutlined,
  FileOutlined,
  SolutionOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MailOutlined,
  CalendarOutlined,
  MoreOutlined,
  EyeOutlined,
  AreaChartOutlined,
  ApartmentOutlined,
  FieldTimeOutlined
} from "@ant-design/icons"
import { useSuperadmin } from "../hooks/useSuperadmin"
import { DesempenhoMetasSpreadsheet } from "../components/DesempenhoMetasSpreadsheet"
import { ConsultoresTeamPanel } from "@/features/consultores/components/ConsultoresTeamPanel"
import { EquipeUnifiedPanel } from "@/components/equipe/EquipeUnifiedPanel"
import { useSuperadminTenants } from "../hooks/useSuperadminTenants"
import { useSuperadminUsers } from "../hooks/useSuperadminUsers"
import { useUsuariosPendentes } from "../hooks/useUsuariosPendentes"
import { TenantList } from "../components/TenantList"
import { UserList } from "../components/UserList"
import { UserWithTenant } from "../types"
import { toast } from "sonner"
import { PageHeader } from "@/components/layout/PageHeader"
import { useAuth } from "@/features/auth"
import { UsoSistemaPanel } from "../components/uso/UsoSistemaPanel"

const { Title, Paragraph } = Typography
const { useBreakpoint } = Grid

export default function SuperadminUnifiedPage() {
  const { token } = theme.useToken()
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const [searchParams, setSearchParams] = useSearchParams()

  // Hooks para gestão de usuários
  const {
    users,
    loading: usersLoading,
    stats,
    fetchUsers,
    fetchDashboardStats,
    createUser,
    updateUser,
    checkUserDependencies,
    deleteUser
  } = useSuperadmin()

  const { tenants, loading: tenantsLoading, fetchTenants, createTenant, updateTenant, toggleTenantStatus, deleteTenant } = useSuperadminTenants()
  const { resetPassword } = useSuperadminUsers()

  // Hooks para usuários pendentes
  const {
    usuariosPendentes,
    isLoading: isPendingLoading,
    aprovarUsuario,
    rejeitarUsuario,
    atualizarTenant: updatePendingTenant,
    atualizarRole: updatePendingRole,
    carregarSupervisores,
    isAprovando,
    isRejeitando,
  } = useUsuariosPendentes()

  const [equipeView, setEquipeView] = useState(() =>
    searchParams.get('tab') === 'times' || searchParams.get('view') === 'time'
      ? 'time'
      : 'lista'
  )
  const { state: { user: usuarioLogado } } = useAuth()
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam === 'uso') return '6'
    if (tabParam === 'pendentes') return '4'
    if (tabParam === 'desempenho') return '5'
    if (tabParam === 'times' || tabParam === 'usuarios' || tabParam === 'equipe') return '3'
    return '1'
  })

  const loading = usersLoading || tenantsLoading || isPendingLoading

  useEffect(() => {
    fetchUsers()
    fetchDashboardStats()
    fetchTenants()
  }, [fetchUsers, fetchDashboardStats, fetchTenants])

  useEffect(() => {
    const tabParam = searchParams.get('tab')
    const viewParam = searchParams.get('view')
    if (tabParam === 'uso') setActiveTab('6')
    else if (tabParam === 'pendentes') setActiveTab('4')
    else if (tabParam === 'desempenho') setActiveTab('5')
    else if (tabParam === 'times' || tabParam === 'usuarios' || tabParam === 'equipe') {
      setActiveTab('3')
      setEquipeView(tabParam === 'times' || viewParam === 'time' ? 'time' : 'lista')
    }
  }, [searchParams])

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    if (tab === '3') {
      setSearchParams({
        tab: 'equipe',
        ...(equipeView === 'time' ? { view: 'time' } : {}),
      })
    } else if (tab === '4') {
      setSearchParams({ tab: 'pendentes' })
    } else if (tab === '5') {
      setSearchParams({ tab: 'desempenho' })
    } else if (tab === '6') {
      setSearchParams({ tab: 'uso' })
    } else {
      setSearchParams({})
    }
  }

  const handleEquipeViewChange = (view: 'lista' | 'time') => {
    setEquipeView(view)
    setSearchParams({
      tab: 'equipe',
      ...(view === 'time' ? { view: 'time' } : {}),
    })
  }

  // Converter usuários para UserWithTenant
  const typedUsers = useMemo(() => {
    return (users || []).map(u => ({
      ...u,
      id: u.id.toString(),
      status: u.status as any,
      username: u.username || '',
      tenant_id: u.tenant_id ? u.tenant_id.toString() : null,
      tenant: u.tenant ? { id: u.tenant.id.toString(), nome: u.tenant.nome } : undefined
    })) as UserWithTenant[]
  }, [users])

  if (loading && !typedUsers.length && usuariosPendentes.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Carregando Painel...">
          <div style={{ padding: 50 }} />
        </Spin>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 transition-colors duration-300" style={{ backgroundColor: token.colorBgLayout }}>
      <PageHeader
        heroic={true}
        title="Administração"
        heroLabel="Sistema"
        stats={[
          { label: 'Usuários', value: stats?.users || 0 },
          { label: 'Empresas', value: stats?.tenants || 0 },
          ...(usuariosPendentes.length > 0 ? [{ label: 'Pendentes', value: usuariosPendentes.length }] : []),
        ]}
      />

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto">
        <div className="px-3 sm:px-4 md:px-6 xl:px-8 py-6">
          <div className="w-full max-w-[2000px] mx-auto">
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              className="rounded-[12px] overflow-hidden border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 w-full [&_.ant-tabs-nav]:w-full [&_.ant-tabs-nav-list]:w-full [&_.ant-tabs-tab]:flex-1 [&_.ant-tabs-tab]:justify-center"
              items={[
                {
                  key: '1',
                  label: (
                    <div className="flex items-center gap-3 py-1">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 dark:bg-zinc-800 text-base text-[#FF3737] dark:text-[#FF3737]">
                        <BarChartOutlined />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-semibold text-gray-800 dark:text-zinc-200">Dashboard</span>
                        <span className="text-xs text-gray-500 dark:text-zinc-400">Visão geral do sistema</span>
                      </div>
                    </div>
                  ),
                  children: (
                    <div className="p-6" style={{ backgroundColor: token.colorBgLayout }}>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                          <Card variant="borderless" style={{ boxShadow: token.boxShadow, backgroundColor: token.colorBgContainer }}>
                            <Statistic
                              title="Total de Usuários"
                              value={stats?.users || 0}
                              prefix={<UserOutlined style={{ color: token.colorPrimary }} />}
                            />
                          </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Card variant="borderless" style={{ boxShadow: token.boxShadow, backgroundColor: token.colorBgContainer }}>
                            <Statistic
                              title="Total de Empresas"
                              value={stats?.tenants || 0}
                              prefix={<BankOutlined style={{ color: token.colorSuccess }} />}
                            />
                          </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Card variant="borderless" style={{ boxShadow: token.boxShadow, backgroundColor: token.colorBgContainer }}>
                            <Statistic
                              title="Total de Clientes"
                              value={stats?.clientes || 0}
                              prefix={<TeamOutlined style={{ color: token.colorPurple }} />}
                            />
                          </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Card variant="borderless" style={{ boxShadow: token.boxShadow, backgroundColor: token.colorBgContainer }}>
                            <Statistic
                              title="Total de Contratos"
                              value={stats?.contratos || 0}
                              prefix={<FileOutlined style={{ color: token.colorWarning }} />}
                            />
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  ),
                },
                {
                  key: '2',
                  label: (
                    <div className="flex items-center gap-3 py-1">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 dark:bg-zinc-800 text-base text-indigo-600 dark:text-indigo-400">
                        <BankOutlined />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-semibold text-gray-800 dark:text-zinc-200">Empresas</span>
                        <span className="text-xs text-gray-500 dark:text-zinc-400">Gerenciar empresas</span>
                      </div>
                    </div>
                  ),
                  children: (
                    <div className="p-6" style={{ backgroundColor: token.colorBgLayout }}>
                      <TenantList
                        tenants={(tenants || []) as any}
                        onToggleStatus={toggleTenantStatus}
                        onCreateTenant={createTenant}
                        onUpdateTenant={async (id, data) => updateTenant({ ...data, id })}
                        onDeleteTenant={deleteTenant}
                      />
                    </div>
                  ),
                },
                {
                  key: '3',
                  label: (
                    <div className="flex items-center gap-3 py-1">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 dark:bg-zinc-800 text-base text-blue-600 dark:text-blue-400">
                        <TeamOutlined />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-semibold text-gray-800 dark:text-zinc-200">Equipe</span>
                        <span className="text-xs text-gray-500 dark:text-zinc-400">Membros e hierarquia</span>
                      </div>
                    </div>
                  ),
                  children: (
                    <div className="p-6" style={{ backgroundColor: token.colorBgLayout }}>
                      <EquipeUnifiedPanel
                        view={equipeView as 'lista' | 'time'}
                        onViewChange={handleEquipeViewChange}
                        listContent={
                          <UserList
                            users={typedUsers}
                            onCreateUser={createUser}
                            onUpdateUser={async (userData) => {
                              const { id, ...data } = userData
                              return updateUser(id, data)
                            }}
                            onDeleteUser={deleteUser}
                            onCheckUserDependencies={checkUserDependencies}
                            onResetPassword={resetPassword}
                            onRefresh={() => {
                              fetchUsers()
                              fetchDashboardStats()
                            }}
                            tenants={tenants?.map(t => ({ id: t.id.toString(), nome: t.nome })) || []}
                          />
                        }
                        teamContent={<ConsultoresTeamPanel embedded allowCreate={false} />}
                      />
                    </div>
                  ),
                },
                {
                  key: '4',
                  label: (
                    <div className="flex items-center gap-3 py-1">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 dark:bg-zinc-800 text-base text-amber-600 dark:text-amber-400">
                        <SolutionOutlined />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-semibold text-gray-800 dark:text-zinc-200">
                          Solicitações
                          {usuariosPendentes.length > 0 && (
                            <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 rounded-full bg-amber-500 text-white text-xs font-bold">
                              {usuariosPendentes.length}
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-zinc-400">Aprovação de usuários</span>
                      </div>
                    </div>
                  ),
                  children: (
                    <div className="p-6" style={{ backgroundColor: token.colorBgLayout }}>
                      {usuariosPendentes.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12">
                          <div style={{ color: token.colorSuccess, marginBottom: 12 }}>
                            <CheckCircleOutlined style={{ fontSize: '48px' }} />
                          </div>
                          <p className="text-base font-semibold mb-1" style={{ color: token.colorText }}>
                            Tudo em dia!
                          </p>
                          <p className="text-sm" style={{ color: token.colorTextSecondary }}>
                            Não há usuários pendentes de aprovação no momento.
                          </p>
                        </div>
                      ) : (
                        <>
                          {usuariosPendentes.length > 0 && (
                            <Alert
                              message={`${usuariosPendentes.length} usuário${usuariosPendentes.length !== 1 ? 's' : ''} aguardando aprovação`}
                              description="Configure empresa, função e supervisor (se aplicável) para cada usuário antes de aprovar."
                              type="info"
                              showIcon
                              className="mb-4"
                            />
                          )}
                          <div className="overflow-auto">
                            <UsuariosPendentesContent
                              usuariosPendentes={usuariosPendentes}
                              tenants={tenants || []}
                              onApprove={aprovarUsuario}
                              onReject={rejeitarUsuario}
                              onTenantChange={updatePendingTenant}
                              onRoleChange={updatePendingRole}
                              onLoadSupervisores={carregarSupervisores}
                              isAprovando={isAprovando}
                              isRejeitando={isRejeitando}
                              isMobile={isMobile}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  ),
                },
                {
                  key: '5',
                  label: (
                    <div className="flex items-center gap-3 py-1">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 dark:bg-zinc-800 text-base text-emerald-600 dark:text-emerald-400">
                        <AreaChartOutlined />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-semibold text-gray-800 dark:text-zinc-200">Desempenho</span>
                        <span className="text-xs text-gray-500 dark:text-zinc-400">Metas + Comissões</span>
                      </div>
                    </div>
                  ),
                  children: (
                    <div className="p-6" style={{ backgroundColor: token.colorBgLayout }}>
                      <DesempenhoMetasSpreadsheet
                        tenants={(tenants || []).map(t => ({
                          id: Number(t.id),
                          nome: t.nome,
                          tipo_unidade: t.tipo_unidade,
                        }))}
                        onRefresh={() => {
                          fetchDashboardStats()
                        }}
                      />
                    </div>
                  ),
                },
                {
                  key: '6',
                  label: (
                    <div className="flex items-center gap-3 py-1">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 dark:bg-zinc-800 text-base text-sky-600 dark:text-sky-400">
                        <FieldTimeOutlined />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-semibold text-gray-800 dark:text-zinc-200">Uso do sistema</span>
                        <span className="text-xs text-gray-500 dark:text-zinc-400">Quem está usando</span>
                      </div>
                    </div>
                  ),
                  children: (
                    <div className="p-6" style={{ backgroundColor: token.colorBgLayout }}>
                      <UsoSistemaPanel tenantId={usuarioLogado?.tenant_id ?? null} />
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// Componente auxiliar para a seção de usuários pendentes

const ROLES_OPTIONS = [
  { value: 'SUPER_ADMIN', label: "Super Administrador" },
  { value: 'REPRESENTANTE', label: "Representante (administrador)" },
  { value: 'ASSOCIADO', label: "Associado" },
  { value: 'SECRETARIA', label: "Secretaria" },
  { value: 'SUPERVISOR', label: "Supervisor" },
  { value: 'RELACIONADOR', label: "Relacionador" },
  { value: 'LIDER_VENDAS', label: "Líder de Vendas" },
  { value: 'POS_VENDA', label: "Pós-Venda" },
  { value: 'USER', label: "Usuário" }
] as const

function UsuariosPendentesContent({
  usuariosPendentes,
  tenants,
  onApprove,
  onReject,
  onTenantChange,
  onRoleChange,
  onLoadSupervisores,
  isAprovando,
  isRejeitando,
  isMobile
}: {
  usuariosPendentes: any[]
  tenants: any[]
  onApprove: (data: any) => Promise<any>
  onReject: (data: any) => Promise<any>
  onTenantChange: (data: any) => void
  onRoleChange: (data: any) => void
  onLoadSupervisores: (tenantId: string) => void
  isAprovando: boolean
  isRejeitando: boolean
  isMobile: boolean
}) {
  const { token } = theme.useToken();
  const [selectedUserForView, setSelectedUserForView] = useState<any | null>(null)
  const [processingUsers, setProcessingUsers] = useState<Record<string, boolean>>({})
  const [selectedSupervisors, setSelectedSupervisors] = useState<Record<string, number | null>>({})
  const [supervisores, setSupervisores] = useState<any[]>([])
  const [tenantSupervisoresCarregados, setTenantSupervisoresCarregados] = useState<string | null>(null)

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return "Data inválida"
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    } catch (error) {
      return "Data inválida"
    }
  }

  const getRoleLabel = (roleValue?: string | null) => {
    return ROLES_OPTIONS.find(r => r.value === roleValue)?.label || "Não definido"
  }

  const handleTenantChange = (userId: string, tenantId: string) => {
    onTenantChange({ userId, tenantId })
    onLoadSupervisores(tenantId)
    setTenantSupervisoresCarregados(tenantId)
    setSelectedSupervisors(prev => ({
      ...prev,
      [userId]: null
    }))
  }

  const handleRoleChange = (userId: string, role: string) => {
    onRoleChange({ userId, role })
    if (!['RELACIONADOR', 'LIDER_VENDAS'].includes(role)) {
      setSelectedSupervisors(prev => ({
        ...prev,
        [userId]: null
      }))
    }
  }

  const approveUser = async (userId: string) => {
    const user = usuariosPendentes.find(u => u.id === userId)
    const selectedSupervisor = selectedSupervisors[userId]

    if (!user) {
      toast.error("Usuário não encontrado")
      return
    }

    if (!user.selectedTenant) {
      toast.error("Selecione uma empresa para o usuário")
      return
    }

    if (!user.selectedRole) {
      toast.error("Selecione uma função para o usuário")
      return
    }

    const rolesQuePrecisamSupervisor = ['RELACIONADOR', 'LIDER_VENDAS']
    if (rolesQuePrecisamSupervisor.includes(user.selectedRole) && !selectedSupervisor) {
      toast.error("Selecione um supervisor para este usuário")
      return
    }

    setProcessingUsers(prev => ({ ...prev, [userId]: true }))

    try {
      await onApprove({
        userId,
        tenantId: user.selectedTenant,
        role: user.selectedRole,
        supervisorId: selectedSupervisor || undefined
      })
    } catch (error) {
      console.error("Erro ao aprovar usuário:", error)
    } finally {
      setProcessingUsers(prev => ({ ...prev, [userId]: false }))
    }
  }

  const rejectUser = async (userId: string) => {
    const user = usuariosPendentes.find(u => u.id === userId)

    if (!user) {
      toast.error("Usuário não encontrado")
      return
    }

    setProcessingUsers(prev => ({ ...prev, [userId]: true }))

    try {
      await onReject({ userId })
    } catch (error) {
      console.error("Erro ao rejeitar usuário:", error)
    } finally {
      setProcessingUsers(prev => ({ ...prev, [userId]: false }))
    }
  }

  const columns = [
    {
      title: 'Usuário',
      key: 'user',
      render: (_: any, record: any) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 'bold', color: token.colorText }}>{record.first_name} {record.last_name}</span>
          <span style={{ fontSize: '12px', color: token.colorTextSecondary }}>
            <MailOutlined style={{ marginRight: 4 }} />
            {record.email}
          </span>
        </Space>
      ),
    },
    {
      title: 'Data Cadastro',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => (
        <Space size={4}>
          <CalendarOutlined style={{ color: token.colorTextSecondary }} />
          {formatDate(date)}
        </Space>
      ),
    },
    {
      title: 'Empresa',
      key: 'tenant',
      render: (_: any, record: any) => {
        const selectedValue = record.selectedTenant ? String(record.selectedTenant) : undefined;
        const empresaVinculada = Boolean(record.tenant_id);
        return (
          <Select
            style={{ width: 180 }}
            placeholder="Selecione empresa"
            value={selectedValue}
            onChange={(value) => handleTenantChange(record.id, value)}
            disabled={processingUsers[record.id] || empresaVinculada}
            options={tenants.map(t => ({ label: t.nome, value: String(t.id) }))}
          />
        );
      },
    },
    {
      title: 'Função',
      key: 'role',
      render: (_: any, record: any) => (
        <Select
          style={{ width: 160 }}
          placeholder="Selecione função"
          value={record.selectedRole || undefined}
          onChange={(value) => handleRoleChange(record.id, value)}
          disabled={processingUsers[record.id] || !record.selectedTenant}
          options={[...ROLES_OPTIONS]}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      align: 'center' as const,
      render: (_: any, record: any) => {
        const isProcessing = processingUsers[record.id] || isAprovando || isRejeitando

        const actionMenu = (
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            <Button
              type="text"
              block
              style={{ textAlign: 'left', display: 'flex', alignItems: 'center' }}
              icon={<EyeOutlined />}
              onClick={() => setSelectedUserForView(record)}
            >
              Visualizar
            </Button>

            <Divider style={{ margin: '4px 0', borderColor: token.colorBorder }} />

            <Popconfirm
              title="Aprovar Usuário"
              description="Deseja realmente aprovar este usuário?"
              onConfirm={() => approveUser(record.id)}
              okText="Sim"
              cancelText="Não"
              disabled={
                !record.selectedTenant ||
                !record.selectedRole
              }
            >
              <Button
                type="text"
                block
                style={{ textAlign: 'left', display: 'flex', alignItems: 'center', color: token.colorSuccess }}
                icon={<CheckCircleOutlined />}
                disabled={
                  !record.selectedTenant ||
                  !record.selectedRole
                }
              >
                Aprovar
              </Button>
            </Popconfirm>

            <Popconfirm
              title="Rejeitar Usuário"
              description="Deseja realmente rejeitar este usuário?"
              onConfirm={() => rejectUser(record.id)}
              okText="Sim"
              cancelText="Não"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                type="text"
                block
                style={{ textAlign: 'left', display: 'flex', alignItems: 'center' }}
                icon={<CloseCircleOutlined />}
              >
                Rejeitar
              </Button>
            </Popconfirm>
          </Space>
        )

        if (isProcessing) return <Spin size="small" />

        return (
          <Popover
            content={actionMenu}
            trigger="click"
            placement="bottomRight"
            arrow={false}
            overlayInnerStyle={{ padding: 4, minWidth: 120 }}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Popover>
        )
      },
    },
  ]

  return (
    <>
      {isMobile ? (
        <List
          dataSource={usuariosPendentes}
          renderItem={(user: any) => (
            <List.Item
              style={{ padding: '16px', borderBottom: `1px solid ${token.colorBorder}` }}
              actions={[
                <Button key="view" type="link" icon={<EyeOutlined />} onClick={() => setSelectedUserForView(user)}>Ver</Button>,
              ]}
            >
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} />}
                title={<span style={{ fontWeight: 'bold', color: token.colorText }}>{user.first_name} {user.last_name}</span>}
                description={
                  <Space direction="vertical" size={2} style={{ width: '100%', marginTop: 8 }}>
                    <span style={{ fontSize: '12px', color: token.colorTextSecondary }}><MailOutlined /> {user.email}</span>
                    <span style={{ fontSize: '12px', color: token.colorTextSecondary }}><CalendarOutlined /> {formatDate(user.created_at)}</span>

                    <Divider style={{ margin: '8px 0', borderColor: token.colorBorder }} />

                    <span style={{ fontWeight: 'bold', fontSize: '12px', color: token.colorText }}>Empresa:</span>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Selecione empresa"
                      value={user.selectedTenant ? String(user.selectedTenant) : undefined}
                      onChange={(value) => handleTenantChange(user.id, value)}
                      disabled={Boolean(user.tenant_id) || processingUsers[user.id]}
                      options={tenants.map(t => ({ label: t.nome, value: String(t.id) }))}
                    />

                    <span style={{ fontWeight: 'bold', fontSize: '12px', marginTop: 8, color: token.colorText }}>Função:</span>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Selecione função"
                      value={user.selectedRole || undefined}
                      onChange={(value) => handleRoleChange(user.id, value)}
                      disabled={!user.selectedTenant}
                      options={[...ROLES_OPTIONS]}
                    />
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={usuariosPendentes}
          rowKey="id"
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
        />
      )}

      {/* Details Modal */}
      <Modal
        title={
          <Space>
            <UserOutlined />
            <span>Detalhes do Usuário</span>
          </Space>
        }
        open={!!selectedUserForView}
        onCancel={() => setSelectedUserForView(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedUserForView(null)}>
            Fechar
          </Button>
        ]}
        width={700}
        styles={{
          content: { backgroundColor: token.colorBgContainer },
          header: { backgroundColor: token.colorBgContainer, borderColor: token.colorBorder },
          footer: { backgroundColor: token.colorBgContainer, borderColor: token.colorBorder }
        }}
      >
        {selectedUserForView && (
          <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} style={{ backgroundColor: token.colorBgBase }}>
            <Descriptions.Item label="Nome Completo" span={2}>
              {selectedUserForView.first_name} {selectedUserForView.last_name}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {selectedUserForView.email}
            </Descriptions.Item>
            <Descriptions.Item label="Data de Cadastro">
              {formatDate(selectedUserForView.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Status Atual">
              <Tag color="orange">PENDENTE</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="ID do Usuário">
              <span style={{ fontSize: '12px', fontFamily: 'monospace', color: token.colorTextSecondary }}>{selectedUserForView.id}</span>
            </Descriptions.Item>

            <Descriptions.Item label="Configurações Selecionadas" span={2}>
              <Space direction="vertical">
                <span style={{ color: token.colorText }}>
                  <BankOutlined /> <strong>Empresa:</strong> {
                    tenants.find(t => String(t.id) === String(selectedUserForView.selectedTenant))?.nome ||
                    <span style={{ color: token.colorError }}>Não selecionada</span>
                  }
                </span>
                <span style={{ color: token.colorText }}>
                  <SolutionOutlined /> <strong>Função:</strong> {getRoleLabel(selectedUserForView.selectedRole)}
                </span>
              </Space>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  )
}
